/**
 * UI kit text (FE-02 FR-FE02-21, FE-03 FR-FE03-05): components take their strings from the `ui`
 * namespace (`packages/frontend/ui/locales/<lang>/ui.json`) or from props. The app plugs its
 * i18next instance in with `<UiTextProvider t exists>`; without a provider (unit tests, isolated
 * use) the bundled es-CR catalog answers `ui:` keys.
 */
import { type ReactNode, createContext, useContext, useMemo } from "react";

import esCR from "../locales/es-CR/ui.json";

export type UiTranslate = (key: string, values?: Record<string, unknown>) => string;

export interface UiText {
  /** Full keys with namespace: `ui:sheet.close`, `common:enums.billing.active`. */
  t: UiTranslate;
  exists: (key: string) => boolean;
  /** An enum value without a label (FE-03 FR-FE03-29: raw value shown, reported once). */
  onUnknownValue?: (kind: string, value: string) => void;
}

function lookup(key: string): string | undefined {
  const [ns, path] = key.includes(":") ? key.split(":", 2) : ["ui", key];
  if (ns !== "ui" || !path) return undefined;
  let node: unknown = esCR;
  for (const part of path.split(".")) {
    node =
      typeof node === "object" && node !== null
        ? (node as Record<string, unknown>)[part]
        : undefined;
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(text: string, values?: Record<string, unknown>): string {
  return values
    ? text.replace(/\{\{(\w+)\}\}/g, (_m, name: string) => String(values[name] ?? ""))
    : text;
}

const FALLBACK: UiText = {
  t: (key, values) => interpolate(lookup(key) ?? key, values),
  exists: (key) => lookup(key) !== undefined,
};

const Context = createContext<UiText>(FALLBACK);

export function UiTextProvider({
  t,
  exists,
  onUnknownValue,
  children,
}: UiText & { children: ReactNode }) {
  const value = useMemo(() => ({ t, exists, onUnknownValue }), [t, exists, onUnknownValue]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useUiText(): UiText {
  return useContext(Context);
}

/** `t` for the kit's own strings: `ui("sheet.close")` → `ui:sheet.close`. */
export function useUiT(): (key: string, values?: Record<string, unknown>) => string {
  const { t } = useUiText();
  return (key, values) => t(`ui:${key}`, values);
}
